import { type FC, type ReactNode } from "react";
import { Tooltip } from "antd";
import { useTranslation } from "react-i18next";
import { formatTranslation } from "../../../../shared/utils/translationUtils";
import { ReactComponent as InfoIcon } from "../../../../assets/images/info-14.svg";
import StdRateFields, { type StdRateOption } from "./StdRateFields";
import "./style.less";

export type StdSeedRateOption = StdRateOption;

export interface StdSeedRateProps {
  productOptions?: StdSeedRateOption[];
  productValue?: string;
  onProductChange?: (value: string) => void;
  seedRateValue?: number | null;
  onSeedRateChange?: (value: number | null) => void;
  title?: string;
  tooltip?: string;
  productLabel?: string;
  seedRateLabel?: string;
  seedRateError?: string;
  // Replaces the single product/rate row, e.g. with several fertiliser rows.
  fields?: ReactNode;
  children?: ReactNode;
  className?: string;
}

const StdSeedRate: FC<StdSeedRateProps> = ({
  productOptions = [],
  productValue,
  onProductChange,
  seedRateValue,
  onSeedRateChange,
  title,
  tooltip,
  productLabel,
  seedRateLabel,
  seedRateError,
  fields,
  children,
  className,
}) => {
  const { t } = useTranslation();

  return (
    <div
      className={`rd-std-seed-rate ${className ?? ""}`}
      data-testid="std-seed-rate"
    >
      <div className="rd-std-seed-rate__title-row">
        <span className="rd-std-seed-rate__title">
          {title ?? formatTranslation(t("newRatesAndDosage.stdSeedRate.title"))}
        </span>
        <Tooltip
          title={
            tooltip ??
            formatTranslation(t("newRatesAndDosage.stdSeedRate.tooltip"))
          }
        >
          <span
            className="rd-std-seed-rate__info-icon"
            data-testid="std-seed-rate-info"
            tabIndex={0}
          >
            <InfoIcon aria-hidden />
          </span>
        </Tooltip>
      </div>
      {fields ?? (
        <StdRateFields
          productLabel={
            productLabel ??
            formatTranslation(t("newRatesAndDosage.stdSeedRate.product"))
          }
          productPlaceholder={formatTranslation(
            t("newRatesAndDosage.stdSeedRate.productPlaceholder")
          )}
          productOptions={productOptions}
          productValue={productValue}
          onProductChange={onProductChange}
          rateLabel={
            seedRateLabel ??
            formatTranslation(t("newRatesAndDosage.stdSeedRate.seedRate"))
          }
          rateValue={seedRateValue}
          onRateChange={onSeedRateChange}
          rateError={seedRateError}
        />
      )}
      {children}
    </div>
  );
};

export default StdSeedRate;
